/**
 * The second half of a sign-in: a code, or an authenticator to set up first.
 *
 * One component for both, because they are the same screen with a QR code above it. A
 * dispatcher who has already enrolled types six digits; one who has not scans first and then
 * types six digits, which is the same muscle memory a day later.
 *
 * There is deliberately no way past it. Section 06 calls MFA mandatory for these accounts,
 * and a "remind me later" would make it a checkbox — so the only controls here are the code
 * box and starting the sign-in again.
 *
 * The recovery codes are the delicate part. They come back exactly once, on the request that
 * completes enrolment, and there is no second read of them anywhere in the system — so they
 * are shown until the person says they have written them down, and then they are gone with
 * the component.
 *
 * The same centred card as the sign-in screen, on the same tokens, and like it this renders
 * before any firm has been resolved: it themes from the stored preference alone.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';

import { ApiError, errorKey, request, type Schema } from '@taxi/api-client';

import { Banner, Button, Card, Field, Input, PageHeader } from '../ui';

/** What the sign-in came back with instead of a session. */
export type MfaChallenge = Schema<'MfaChallengeView'>;

type Enrolment = Schema<'Enrolment'>;

type Verified = Schema<'MfaVerified'>;

interface Props {
  challenge: MfaChallenge;

  /**
   * `/api/auth` at a firm, `/api/platform` above them.
   *
   * The two surfaces have their own pair of endpoints for the same reason their sign-ins are
   * separate: a platform session resolves no tenant.
   */
  prefix: string;

  /** Hands the finished session to whoever started the sign-in. */
  onSignedIn: (session: { accessToken: string; refreshToken: string }) => void;

  /** Abandons the half-finished sign-in and goes back to the password screen. */
  onCancel: () => void;
}

export function SecondFactor({ challenge, prefix, onSignedIn, onCancel }: Props) {
  const { t } = useTranslation();

  const [secret, setSecret] = useState<Enrolment | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [session, setSession] = useState<Verified | null>(null);
  const [copied, setCopied] = useState(false);

  function say(failure: unknown, fallback: string): string {
    return failure instanceof ApiError
      ? t(errorKey(failure.problem), {
          ...failure.problem.arguments,
          defaultValue: t(fallback),
        })
      : t(fallback);
  }

  // The secret, and the QR code that carries it. Fetched once on arrival rather than behind
  // a button: somebody who has just been told they must enrol has nothing else to do here.
  useEffect(() => {
    let cancelled = false;

    if (challenge.enrolled) return undefined;

    void (async () => {
      try {
        const enrolment = await request<Enrolment>(`${prefix}/mfa/setup`, {
          method: 'POST',
          body: { challengeToken: challenge.challengeToken },
        });

        if (cancelled) return;

        setSecret(enrolment);
        setQr(await QRCode.toDataURL(enrolment.otpAuthUri, { margin: 1, width: 220 }));
      } catch (failure) {
        if (!cancelled) setError(say(failure, 'mfa.couldNotStart'));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge.challengeToken, challenge.enrolled, prefix]);

  async function submit(event: FormEvent) {
    event.preventDefault();

    setBusy(true);
    setError(null);

    try {
      const verified = await request<Verified>(`${prefix}/mfa/verify`, {
        method: 'POST',
        body: { challengeToken: challenge.challengeToken, code: code.trim() },
      });

      // Recovery codes mean this request was the enrolment, and they are readable exactly
      // here. Holding the session back until the person has acknowledged them is the only
      // thing that stops the screen from replacing itself before they have been read.
      if (verified.recoveryCodes !== null && verified.recoveryCodes.length > 0) {
        setRecoveryCodes(verified.recoveryCodes);
        setSession(verified);
        return;
      }

      onSignedIn(verified);
    } catch (failure) {
      setError(say(failure, 'mfa.couldNotVerify'));
      setCode('');
    } finally {
      setBusy(false);
    }
  }

  /**
   * The clipboard, and what happens when the browser refuses it.
   *
   * A console served over plain HTTP has no `navigator.clipboard`, and a refusal there must
   * not look like a copy that worked — the codes are on screen to be written down either
   * way, so the button simply stays as it was.
   */
  async function copy(codes: string[]) {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  if (recoveryCodes !== null && session !== null) {
    return (
      <main className="min-h-screen grid place-items-center p-6">
        <Card className="w-[min(360px,100%)]">
          <div className="flex flex-col gap-3.5 p-6">
            <PageHeader title={t('mfa.recovery.title')} />

            <Banner tone="warn">{t('mfa.recovery.once')}</Banner>

            <ul className="m-0 list-none p-3 flex flex-col gap-1 bg-surface-2 border border-rule rounded-control font-mono text-table tracking-[0.03em] font-semibold">
              {recoveryCodes.map((recoveryCode) => (
                <li key={recoveryCode}>{recoveryCode}</li>
              ))}
            </ul>

            <Button size="sm" className="self-start" onClick={() => void copy(recoveryCodes)}>
              {copied ? t('mfa.recovery.copied') : t('mfa.recovery.copy')}
            </Button>

            <p className="text-secondary text-ink-3 m-0">{t('mfa.recovery.what')}</p>

            {/* The one control that lets go of the session, and it is deliberately the only
                way out of this screen. */}
            <Button
              variant="primary"
              className="w-full"
              onClick={() => onSignedIn(session)}
            >
              {t('mfa.recovery.saved')}
            </Button>
          </div>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen grid place-items-center p-6">
      <Card className="w-[min(360px,100%)]">
        <form className="flex flex-col gap-3.5 p-6" onSubmit={(event) => void submit(event)}>
          <PageHeader
            title={challenge.enrolled ? t('mfa.title') : t('mfa.enrol.title')}
            description={challenge.enrolled ? undefined : t('mfa.enrol.why')}
          />

          {!challenge.enrolled && (
            <>
              {/* White in both schemes, because a QR code needs a quiet zone to scan at
                  all — which is what `--qr-ground` is for. */}
              {qr !== null && (
                <div className="self-center bg-qr-ground rounded-control p-2">
                  <img src={qr} alt={t('mfa.enrol.qrAlt')} width={220} height={220} />
                </div>
              )}

              {secret !== null && (
                <div className="flex flex-col gap-1">
                  <span className="text-secondary text-ink-3">{t('mfa.enrol.orType')}</span>
                  <code className="font-mono text-table tracking-[0.03em] font-semibold break-all select-all">
                    {secret.secret}
                  </code>
                </div>
              )}
            </>
          )}

          {error !== null && <Banner tone="danger">{error}</Banner>}

          <Field
            label={t('mfa.code')}
            help={challenge.enrolled ? t('mfa.codeHint') : t('mfa.enrol.codeHint')}
          >
            <Input
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              className="font-mono tracking-[0.2em]"
              autoFocus
              required
            />
          </Field>

          <Button type="submit" variant="primary" disabled={busy} className="w-full">
            {busy ? t('mfa.checking') : t('mfa.submit')}
          </Button>

          {/* Starting again is the only other thing this screen can do. */}
          <Button variant="quiet" className="self-center" onClick={onCancel}>
            {t('mfa.startAgain')}
          </Button>
        </form>
      </Card>
    </main>
  );
}
